/**
 * SparkDrawerPortal — behavioral wrapper around the static Spark `DrawerItem`.
 *
 * Adds the runtime behaviors the Storybook-only Spark Drawer does not provide:
 *   - Portal rendering (via ReactDOM.createPortal to document.body)
 *   - Slide-in / slide-out CSS animation
 *   - Overlay / mask with click-to-close
 *   - Escape-key to close
 *   - Body scroll-lock while open
 *   - `destroyOnClose` — unmounts children after the close animation ends
 *   - `afterOpenChange` — callback fired when the animation completes
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useOverlayEscape } from '../../hooks/useOverlayEscape';
import './SparkDrawerPortal.css';

/** Runtime drawer portal with slide animation, overlay, escape-key close, scroll-lock, and focus trap. */
export type SparkDrawerPortalProps = {
  open: boolean;
  onClose: () => void;
  afterOpenChange?: (open: boolean) => void;
  /** When true the portal root is offset below a 60px navbar. */
  belowNavbar?: boolean;
  mask?: boolean;
  destroyOnClose?: boolean;
  /** Trap focus inside the drawer while open. Defaults to true. */
  focusTrap?: boolean;
  children?: ReactNode;
  className?: string;
};

export function SparkDrawerPortal({
  open,
  onClose,
  afterOpenChange,
  belowNavbar = true,
  mask = true,
  destroyOnClose = true,
  focusTrap: enableFocusTrap = true,
  children,
  className = '',
}: SparkDrawerPortalProps) {
  /**
   * `mounted` tracks whether children should be in the DOM.
   * `visible` drives the CSS open classes (animation).
   *
   * On open:  mount immediately, then flip visible on the next frame so
   *           the browser registers the initial `translateX(100%)` and
   *           the transition fires properly.
   * On close: remove visible first (triggers slide-out), then unmount
   *           after the transition ends (or immediately if destroyOnClose=false).
   */
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  // Avoid stale closure in transitionend handler.
  const openRef = useRef(open);
  openRef.current = open;

  useFocusTrap(panelRef, open && enableFocusTrap);

  // Open sequence: mount → double rAF → make visible
  useEffect(() => {
    if (open) {
      setMounted(true);
      // Two rAFs: the first lets the browser commit the initial layout with the
      // panel off-screen (translateX(100%)); the second fires after that frame is
      // painted, so adding the open class triggers the CSS transition rather than
      // snapping immediately to the open position.
      let inner: number;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => {
          setVisible(true);
        });
      });
      return () => {
        cancelAnimationFrame(outer);
        cancelAnimationFrame(inner);
      };
    }
    // Close sequence: remove visible, unmount handled by transitionend.
    setVisible(false);
    return undefined;
  }, [open]);

  // Fire afterOpenChange and handle destroyOnClose on transition end.
  const handleTransitionEnd = useCallback(() => {
    const isNowOpen = openRef.current;
    afterOpenChange?.(isNowOpen);
    if (!isNowOpen && destroyOnClose) {
      setMounted(false);
    }
  }, [afterOpenChange, destroyOnClose]);

  // Escape key — only closes if this is the topmost overlay (see useOverlayEscape).
  useOverlayEscape(open, onClose);

  // Body scroll lock
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!mounted) return null;

  const rootClasses = [
    'spark-drawer-portal-root',
    belowNavbar ? 'spark-drawer-portal-root--below-navbar' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const maskClasses = [
    'spark-drawer-portal-mask',
    visible ? 'spark-drawer-portal-mask--visible' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const panelClasses = [
    'spark-drawer-portal-panel',
    visible ? 'spark-drawer-portal-panel--open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return createPortal(
    <div className={rootClasses}>
      {mask ? (
        <div
          className={maskClasses}
          onClick={onClose}
          aria-hidden="true"
        />
      ) : null}
      <div
        ref={panelRef}
        className={panelClasses}
        onTransitionEnd={handleTransitionEnd}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
